import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LoadStatus } from '../../../model';
import { fetchReportingMetrics } from './reportingDashboardApi';
import {
    ReportingMetricsRange,
    ReportingMetricsRangeComparison,
} from '../../../model/ReportingMetrics';
import {
    formatDate,
    formatDateUtc,
    serializeLocalAsUtc,
    startOfDay,
} from '../../../i18n';
import { addDays } from '@fluentui/react-calendar-compat';
import { createAppThunk } from '../../thunks';

export type PresetReportingDayRange = 1 | 7 | 14 | 28;
export type ReportingDayRange = 'custom' | PresetReportingDayRange;
export const customReportingDayRangeValue: ReportingDayRange & string =
    'custom';

const defaultDayRange = 7 as PresetReportingDayRange;

const getPresetStartDateString = (range: PresetReportingDayRange): string =>
    addDays(startOfDay(new Date()), -range).toISOString();

export const metricsDateFormat = 'DD/MM/yyyy';

const formatMetricsDate = (dateStr: string, isEnd = false) => {
    if (isEnd) {
        const date = new Date(dateStr);
        date.setDate(date.getDate() - 1);

        return formatDate(date, metricsDateFormat);
    }

    return formatDateUtc(dateStr, metricsDateFormat);
};

const getMetricsDate = (range?: ReportingMetricsRange) => {
    if (!range) {
        return '';
    }

    const start = formatMetricsDate(range.startDateUtc);
    const end = formatMetricsDate(range.endDateUtc, true);

    return `${start} - ${end}`;
};

export interface ReportingDashboardRangeDates {
    start: string;
    end: string | null;
}

export interface ReportingDashboardFilters {
    category: string | null;
    aircraftRegNumber: string | null;
    groundTeamId: {
        value: string | null;
        isLocked: boolean;
    };
}

export interface ReportingDashboardRangeLabels {
    previousDescriptive: string;
    previousDates: string;
    currentDescriptive: string;
    currentDates: string;
}

export interface ReportingDashboardState {
    loadStatus: LoadStatus;
    selectedDayRange: ReportingDayRange;
    selectedRangeDates: ReportingDashboardRangeDates;
    filters: ReportingDashboardFilters;
    previousRange?: ReportingMetricsRange;
    currentRange?: ReportingMetricsRange;
    rangeLabels: ReportingDashboardRangeLabels;
}

const initialState: ReportingDashboardState = {
    loadStatus: 'idle',
    selectedDayRange: defaultDayRange,
    selectedRangeDates: {
        start: getPresetStartDateString(defaultDayRange),
        end: null,
    },
    filters: {
        category: null,
        aircraftRegNumber: null,
        groundTeamId: {
            value: null,
            isLocked: false,
        },
    },
    rangeLabels: {
        previousDescriptive: '',
        currentDescriptive: '',
        previousDates: '',
        currentDates: '',
    },
};

export const loadMetrics = createAppThunk<ReportingMetricsRangeComparison>(
    'reportingDashboard/loadMetrics',
    async (_, { getState }) => {
        const state = getState().reportingDashboard;
        const { aircraftRegNumber, category, groundTeamId } = state.filters;

        const startDate = startOfDay(new Date(state.selectedRangeDates.start));

        const endDate = state.selectedRangeDates.end
            ? addDays(startOfDay(new Date(state.selectedRangeDates.end)), 1)
            : null;

        const response = await fetchReportingMetrics({
            utcStartDate: serializeLocalAsUtc(startDate),
            utcEndDate: endDate ? serializeLocalAsUtc(endDate) : null,
            aircraftRegNumber,
            category,
            groundTeamId: groundTeamId.value,
        });

        return response.data;
    }
);

export const reportingDashboardSlice = createSlice({
    name: 'reportingDashboard',
    initialState,
    reducers: {
        updateCustomRangeDates(
            state: ReportingDashboardState,
            action: PayloadAction<Partial<ReportingDashboardRangeDates>>
        ) {
            const selectedDayRange = 'custom';
            state.selectedDayRange = selectedDayRange;

            const { start, end } = action.payload;

            state.selectedRangeDates.end = end ?? state.selectedRangeDates.end;
            state.selectedRangeDates.start =
                start ?? state.selectedRangeDates.start;

            state.filters = {
                ...state.filters,
                category: null,
                aircraftRegNumber: null,
            };
        },
        setPresetRange(
            state: ReportingDashboardState,
            action: PayloadAction<PresetReportingDayRange>
        ) {
            state.selectedDayRange = action.payload;

            state.selectedRangeDates = {
                start: getPresetStartDateString(action.payload),
                end: null,
            };

            state.filters = {
                ...state.filters,
                category: null,
                aircraftRegNumber: null,
            };
        },
        updateFilters(
            state: ReportingDashboardState,
            action: PayloadAction<
                Partial<Omit<ReportingDashboardFilters, 'groundTeamId'>>
            >
        ) {
            const filters = action.payload;

            if (filters.category !== undefined) {
                state.filters.category = filters.category;
            }

            if (filters.aircraftRegNumber !== undefined) {
                state.filters.aircraftRegNumber = filters.aircraftRegNumber;
            }
        },
        setGroundTeamId(
            state: ReportingDashboardState,
            action: PayloadAction<string | null>
        ) {
            state.filters.groundTeamId = {
                value: action.payload,
                isLocked: false,
            };
        },
        lockGroundTeamId(
            state: ReportingDashboardState,
            action: PayloadAction<string>
        ) {
            state.filters.groundTeamId = {
                value: action.payload,
                isLocked: true,
            };
        },
        setRangeLabels(
            state: ReportingDashboardState,
            action: PayloadAction<{ previous?: string; current?: string }>
        ) {
            const { previous, current } = action.payload;

            state.rangeLabels.previousDescriptive =
                previous ?? state.rangeLabels.previousDescriptive;
            state.rangeLabels.currentDescriptive =
                current ?? state.rangeLabels.currentDescriptive;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadMetrics.pending, (state) => {
                state.loadStatus = 'loading';
            })
            .addCase(loadMetrics.fulfilled, (state, action) => {
                state.loadStatus = 'idle';

                const { previous, current } = action.payload;

                state.previousRange = previous;
                state.currentRange = current;

                state.rangeLabels.previousDates = getMetricsDate(previous);
                state.rangeLabels.currentDates = getMetricsDate(current);
            })
            .addCase(loadMetrics.rejected, (state) => {
                state.loadStatus = 'failed';
            });
    },
});

export const {
    updateCustomRangeDates,
    setPresetRange,
    updateFilters,
    setRangeLabels,
    lockGroundTeamId,
    setGroundTeamId,
} = reportingDashboardSlice.actions;

export default reportingDashboardSlice.reducer;
